import React, { ReactElement, useMemo } from 'react';
import { Pagination as BootstrapPagination } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import Select from 'react-select';

export type PaginationProps = {
  canPreviousPage: boolean;
  canNextPage: boolean;
  previousPage: () => void;
  nextPage: () => void;
  gotoPage: (page: number) => void;
  pageCount: number;
  pageIndex: number;
  showSizeDropdown?: boolean;
  showEllipsis?: boolean;
  showTotalElements?: boolean;
  showPagination?: boolean;
  showEdgeButtons?: boolean;
  size?: number;
  setPageSize?: (pageSize: number) => void;
  pageSize?: number;
  pageSizeOptions?: number[];
  totalElements?: number;
};

export const Pagination: React.FC<React.PropsWithChildren<PaginationProps>> = ({
  canPreviousPage,
  canNextPage,
  previousPage,
  nextPage,
  gotoPage,
  pageCount,
  pageIndex,
  size = 3,
  showSizeDropdown = true,
  showEllipsis = false,
  showTotalElements = true,
  showPagination = true,
  showEdgeButtons = true,
  setPageSize,
  pageSize,
  pageSizeOptions,
  totalElements,
}: PaginationProps) => {
  const { t } = useTranslation();

  const offset = size >> 1;

  const paginationList = useMemo((): ReactElement[] => {
    const minIndex = Math.max(0, Math.min(pageIndex - offset, pageCount - size));
    const nbItems = Math.min(size, pageCount);

    const pages = [];

    for (let index = 0; index < nbItems; index++) {
      const currentIndex = minIndex + index;

      if (currentIndex > pageCount) {
        break;
      }

      pages.push(
        <BootstrapPagination.Item key={currentIndex} active={currentIndex === pageIndex} onClick={() => gotoPage(currentIndex)}>
          {currentIndex + 1}
        </BootstrapPagination.Item>
      );
    }

    return pages;
  }, [gotoPage, offset, pageCount, pageIndex, size]);

  const options = pageSizeOptions?.flatMap((option) => ({
    value: option,
    label: `${option}`,
  }));

  const customStyles = {
    control: (base: any) => ({
      ...base,
      minHeight: '30px',
      height: '30px',
    }),
    valueContainer: (base: any) => ({
      ...base,
      padding: '0 6px',
    }),
    input: (base: any) => ({
      ...base,
      margin: 0,
      padding: 0,
    }),
    indicatorsContainer: (base: any) => ({
      ...base,
      height: '30px',
    }),
  };

  return (
    <div className="table-pagination d-flex align-items-center">
      {showTotalElements && <span> {`${t('table.totalElements')}: ${totalElements} `}</span>}

      {showPagination && (
        <div className="d-flex ms-auto">
          {pageCount > 0 && (
            <>
              {showSizeDropdown && pageSizeOptions && setPageSize && (
                <div className="me-5">
                  <Select
                    defaultValue={options?.find((opt) => opt.value === pageSize)}
                    options={options}
                    onChange={(value) => {
                      setPageSize(Number(value?.value));
                    }}
                    styles={customStyles}
                  />
                </div>
              )}

              <BootstrapPagination>
                {showEdgeButtons && <BootstrapPagination.First onClick={() => gotoPage(0)} disabled={!canPreviousPage} />}
                <BootstrapPagination.Prev onClick={() => previousPage()} disabled={!canPreviousPage} />
                {showEllipsis && pageIndex - offset > 0 && <BootstrapPagination.Ellipsis disabled />}
                {paginationList}
                {showEllipsis && pageIndex + offset < pageCount - 1 && <BootstrapPagination.Ellipsis disabled />}
                <BootstrapPagination.Next onClick={() => nextPage()} disabled={!canNextPage} />
                {showEdgeButtons && <BootstrapPagination.Last onClick={() => gotoPage(pageCount - 1)} disabled={!canNextPage} />}
              </BootstrapPagination>
            </>
          )}
        </div>
      )}
    </div>
  );
};
